/**
 * The post-send chip row (#746 1.1): what happens to the case once this reply is delivered. One row replaces Send &
 * wait, Send & close, the after-send suggestion box and the Chase footer. Nothing here writes: the choice rides the
 * send as `post_send` and the server applies it only after Desk reports the reply delivered.
 */
import { Chip, Input, Popover, PopoverContent, PopoverTrigger } from '@patchbay/ui'
import { ChevronDown } from 'lucide-react'
import type { ChaseIn, NextMoveIn, OwnerKind, PostSendPlan, UpdateTimeIn } from '../../lib/api/generated'
import { formatWhen } from '../../lib/display-time'
import { addBusinessDays, fromLocalInput, toLocalInput } from '../next-move/due'

/** The case's open next move, as the composer needs it to keep a chase on the same owner. */
export interface CaseMoveSnapshot {
  text: string
  ownerKind?: OwnerKind | null
  dueAt?: string | null
  ownerUserId?: string | null
  ownerContractorId?: string | null
  ownerDeskAgentId?: string | null
  moveId?: string | null
}

export type ReplyIntent = 'close' | 'ask' | 'update' | 'resolve'

export interface PostSendChoice {
  action: 'wait_until' | 'close' | 'none'
  /** datetime-local value for Wait until */
  due: string
  /** the "+ time" chip: minutes and whether they bill; null = no time */
  time: { minutes: number; billable: boolean } | null
  /** set when this send is a Chase: the update logs a 'chased' event */
  chase: ChaseIn | null
}

export const FOLLOW_UP_BUSINESS_DAYS = 2

export function defaultDue(now = new Date()): string {
  return toLocalInput(addBusinessDays(now, FOLLOW_UP_BUSINESS_DAYS))
}

/** The chip the row opens on, from the guard's reading of the reply. A person's change always wins. */
export function preselect(
  audience: 'customer' | 'contractor' | 'internal',
  intent: ReplyIntent | null | undefined,
  asksCustomer: boolean,
  caseClosed = false,
): PostSendChoice['action'] {
  if (audience !== 'customer') return 'none'
  // A closed case cannot take Wait until until the send reopens it. Nothing is the safe default.
  if (caseClosed) return 'none'
  if (intent === 'close' || intent === 'resolve') return 'close'
  if (intent === 'ask' && asksCustomer) return 'wait_until'
  return 'none'
}

export function initialChoice(action: PostSendChoice['action'] = 'none', now = new Date()): PostSendChoice {
  return { action, due: defaultDue(now), time: null, chase: null }
}

/**
 * The wire body for a choice. Close is the one frozen close-out that only the Close dialog can build, so the row
 * sends the reply and the case view opens that dialog next; it never closes by itself.
 */
export function toSendFields(
  choice: PostSendChoice,
  audience: 'customer' | 'contractor' | 'internal',
  move: CaseMoveSnapshot | null | undefined,
): { post_send: PostSendPlan | null; after_send_action: 'close' | null } {
  if (choice.action === 'close') return { post_send: null, after_send_action: 'close' }
  const time: UpdateTimeIn | null = choice.time ? { minutes: choice.time.minutes, billable: choice.time.billable } : null
  if (choice.action === 'wait_until') {
    const dueAt = fromLocalInput(choice.due)
    const next = dueAt ? waitMove(audience, move, dueAt) : null
    if (next) {
      return {
        post_send: { action: 'wait_until', next_move: next, time, chase: choice.chase },
        after_send_action: null,
      }
    }
  }
  return { post_send: time ? { action: 'none', time } : null, after_send_action: null }
}

/** The move a Wait until sets: the customer owes the reply, or a chase keeps the move's own text and owner. */
function waitMove(
  audience: 'customer' | 'contractor' | 'internal',
  move: CaseMoveSnapshot | null | undefined,
  dueAt: string,
): NextMoveIn | null {
  if (audience === 'customer') return { text: 'Customer to reply', owner_kind: 'customer', due_at: dueAt }
  if (audience === 'contractor' && move?.text?.trim() && move.ownerKind === 'contractor' && move.ownerContractorId) {
    return { text: move.text.trim(), owner_kind: 'contractor', owner_contractor_id: move.ownerContractorId, due_at: dueAt }
  }
  return null
}

export function dueLabel(due: string): string {
  const date = new Date(due)
  if (Number.isNaN(date.getTime())) return 'pick a time'
  const day = formatWhen(date.toISOString(), { style: 'date' })
  const time = formatWhen(date.toISOString(), { style: 'time' })
  return `${day} ${time}`
}

const OPTIONS: { action: PostSendChoice['action']; label: string }[] = [
  { action: 'wait_until', label: 'Wait until' },
  { action: 'close', label: 'Close' },
  { action: 'none', label: 'Nothing' },
]

export function PostSendChips({
  choice,
  audience,
  chasing = false,
  canWait = true,
  canClose = true,
  caseClosed = false,
  onChange,
}: {
  choice: PostSendChoice
  audience: 'customer' | 'contractor' | 'internal'
  chasing?: boolean
  /** false when no wait can be set (a contractor reply with no contractor-owned move) */
  canWait?: boolean
  /** false on the phone, which has no Close dialog to open after the send */
  canClose?: boolean
  /** #1071: sending on a closed customer case reopens it. The default chip must not need a live case. */
  caseClosed?: boolean
  onChange: (choice: PostSendChoice) => void
}) {
  if (audience === 'internal') return null
  const options = OPTIONS.filter((option) =>
    option.action === 'close' ? audience === 'customer' && canClose : option.action !== 'wait_until' || canWait,
  )
  return (
    <fieldset
      data-testid="post-send-chips"
      className="m-0 flex min-w-0 flex-wrap items-center gap-2 border-0 p-0 text-xs text-muted-foreground"
    >
      <legend className="sr-only">After this reply is delivered</legend>
      {chasing ? <Chip tone="riley">Chase</Chip> : null}
      {caseClosed && audience === 'customer' ? <span data-testid="reopen-on-send">Sending reopens this case.</span> : null}
      <span>After sending:</span>
      {options.map(({ action, label }) =>
        action === 'wait_until' ? (
          <span key={action} className="inline-flex items-center">
            <button
              type="button"
              aria-pressed={choice.action === action}
              className={chipClass(choice.action === action)}
              onClick={() => onChange({ ...choice, action })}
            >
              {label} {dueLabel(choice.due)}
            </button>
            <Popover>
              <PopoverTrigger aria-label="Pick follow-up time" className="ml-0.5 rounded-sm p-0.5 hover:bg-muted">
                <ChevronDown className="size-3" aria-hidden />
              </PopoverTrigger>
              <PopoverContent data-composer-overlay className="w-64 p-2">
                <Input
                  aria-label="Follow-up time"
                  type="datetime-local"
                  value={choice.due}
                  onChange={(event) => onChange({ ...choice, action: 'wait_until', due: event.target.value })}
                />
              </PopoverContent>
            </Popover>
          </span>
        ) : (
          <button
            key={action}
            type="button"
            aria-pressed={choice.action === action}
            className={chipClass(choice.action === action)}
            onClick={() => onChange({ ...choice, action, time: action === 'close' ? null : choice.time })}
          >
            {label}
          </button>
        ),
      )}
      {choice.action !== 'close' ? <TimeChip time={choice.time} onChange={(time) => onChange({ ...choice, time })} /> : null}
    </fieldset>
  )
}

function chipClass(on: boolean): string {
  return `rounded-full border px-2 py-0.5 ${on ? 'border-primary bg-primary/10 text-foreground' : 'hover:bg-muted'}`
}

function TimeChip({ time, onChange }: { time: PostSendChoice['time']; onChange: (time: PostSendChoice['time']) => void }) {
  return (
    <Popover>
      <PopoverTrigger aria-label="Log time with this reply" className={chipClass(time !== null)}>
        {time ? `+ ${time.minutes} min` : '+ time'}
      </PopoverTrigger>
      <PopoverContent data-composer-overlay className="w-56 space-y-2 p-2">
        <Input
          aria-label="Minutes"
          type="number"
          min={1}
          max={1440}
          value={time?.minutes ?? ''}
          placeholder="Minutes"
          onChange={(event) => {
            const minutes = Number.parseInt(event.target.value, 10)
            onChange(Number.isFinite(minutes) && minutes > 0 ? { minutes, billable: time?.billable ?? true } : null)
          }}
        />
        <label className="flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            checked={time?.billable ?? true}
            disabled={!time}
            onChange={(event) => time && onChange({ ...time, billable: event.target.checked })}
          />
          Billable
        </label>
      </PopoverContent>
    </Popover>
  )
}
